import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Container,
  Link,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import Page from 'src/components/Page';
import Auth from 'src/api/auth';
import alertSnackbarActions from 'src/store/actions/alertSnackbarActions';
import { connect } from 'react-redux';
import storage from 'src/storage';
import userActions from 'src/store/actions/userActions';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    height: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  }
}));

const LoginView = (props) => {
  const {
    setMessage, setSeverity, setOpen, updateUser
  } = props;
  const classes = useStyles();

  // httpService sends an ended session here with a full page load, which
  // drops its toast, so say it here instead (ISSUE-0230).
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('session') === 'ended') {
      setSeverity('error');
      setMessage('Your session has ended. Please log in again.');
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async ({ email, password }) => {
    setOpen(false);
    setMessage('');
    try {
      const res = await Auth.signIn({ email, password });
      storage.setUser(res.data);
      updateUser(storage.getUser());
      window.location.replace('/app/dashboard');
    } catch (error) {
      if (error.response) {
        // Request made and server responded
        console.log('Response Error', error.response.data);
        console.log('Response Error', error.response.status);
        console.log('Response Error', error.response.headers);
        const { status, data } = error.response;
        // 400 is a malformed form; a 401 carries the API's own reason, which
        // is either "Invalid email or password" or the account-locked notice.
        // Anything else (429, 5xx) has a real backend message worth showing.
        setMessage(
          status === 400
            ? 'Invalid email or password'
            : data?.errors?.[0] ||
                (status === 401 ? 'Invalid email or password' : data?.message) ||
                'Something went wrong'
        );
      } else if (error.request) {
        // The request was made but no response was received
        console.log('RequestError', error.request);
        setMessage('Network Error');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.log('General Error', error.message);
        setMessage('Network Error');
      }
      setSeverity('error');
      setOpen(true);
    }
  };

  return (
    <Page
      className={classes.root}
      title="Login"
    >
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        justifyContent="center"
      >
        <Container maxWidth="sm">
          <Formik
            initialValues={{
              email: '',
              password: ''
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
              password: Yup.string().max(255).required('Password is required')
            })}
            onSubmit={submit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <Box mb={3}>
                  <Typography
                    color="textPrimary"
                    variant="h2"
                  >
                    Sign in
                  </Typography>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                  >
                    Sign in on the admin platform
                  </Typography>
                </Box>

                <TextField
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  label="Email Address"
                  margin="normal"
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="email"
                  value={values.email}
                  variant="outlined"
                />
                <TextField
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  label="Password"
                  margin="normal"
                  name="password"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="password"
                  value={values.password}
                  variant="outlined"
                />
                <Box my={2}>
                  <Button
                    color="primary"
                    disabled={isSubmitting}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    Sign in now
                  </Button>
                </Box>
                {/* ISSUE-0200 */}
                <Typography
                  color="textSecondary"
                  variant="body1"
                >
                  <Link
                    component={RouterLink}
                    to="/forgot"
                    variant="h6"
                  >
                    Forgot password?
                  </Link>
                </Typography>
              </form>
            )}
          </Formik>
        </Container>
      </Box>
    </Page>
  );
};

LoginView.propTypes = {
  setOpen: PropTypes.func,
  setMessage: PropTypes.func,
  setSeverity: PropTypes.func,
  updateUser: PropTypes.func,
};

const mapStateToProps = (state /* , ownProps */) => {
  return {
    alertSnackbar: state.alertSnackbar
  };
};

const { setOpen, setMessage, setSeverity } = alertSnackbarActions;
const { updateUser } = userActions;

const mapDispatchToProps = {
  setOpen, setMessage, setSeverity, updateUser
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LoginView);
